"use client"

import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { useDispatch } from 'react-redux';
import { Dumbbell, Mail, Lock } from 'lucide-react';

import { AppDispatch } from "@/redux/store";

import { createUser, signInUser } from '../../features/authSlice';

import {
  Form,
  FormItem,
  FormLabel,
} from "../ui/form"
import { Input } from "../ui/input"

interface SignUpFormValues {
  email: string;
  password: string;
}

const formSchema = z.object({
  "email": z.string().min(7, {
    message: "email must be at least 7 characters.",
  }),
  "password": z.string().min(8, {
    message: "Password must be at least 8 characters.",
  }),
});

export const SignUpPage = () => {
  const [activeTab, setActiveTab] = useState<'signin' | 'create'>('signin');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      "email": "",
      "password": "",
    },
  })
  const dispatch: AppDispatch = useDispatch();

  function onSubmit(actionType: 'create' | 'signIn' | null, values: z.infer<typeof formSchema>) {
    const { email, password } = values;
    if (actionType === 'create') {
      dispatch(createUser({ email, password }));
    } else if (actionType === 'signIn') {
      dispatch(signInUser({ email, password }));
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background px-5">
      {/* Logo & Title */}
      <div className="flex flex-col items-center mb-10">
        <div
          className="flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
          style={{ backgroundColor: 'hsl(var(--tmw-accent))' }}
        >
          <Dumbbell className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-foreground">Track My Workout</h1>
        <p className="text-sm text-muted-foreground mt-1">Your personal fitness companion</p>
      </div>

      {/* Tab Switcher */}
      <div className="flex w-full max-w-sm bg-secondary rounded-xl p-1 mb-6">
        <button
          onClick={() => setActiveTab('signin')}
          className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all"
          style={{
            backgroundColor: activeTab === 'signin' ? 'hsl(var(--tmw-accent))' : 'transparent',
            color: activeTab === 'signin' ? 'white' : 'hsl(var(--muted-foreground))'
          }}
        >
          Sign In
        </button>
        <button
          onClick={() => setActiveTab('create')}
          className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all"
          style={{
            backgroundColor: activeTab === 'create' ? 'hsl(var(--tmw-accent))' : 'transparent',
            color: activeTab === 'create' ? 'white' : 'hsl(var(--muted-foreground))'
          }}
        >
          Create Account
        </button>
      </div>

      {/* Form Card */}
      <div className="w-full max-w-sm p-6 rounded-2xl bg-white dark:bg-[hsl(240_5%_16%)] shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit((values) => onSubmit(null, values))} className="space-y-4">
            <FormItem>
              <FormLabel htmlFor="email" className="text-xs font-semibold text-muted-foreground">
                EMAIL
              </FormLabel>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className="h-12 pl-10 rounded-xl border-border bg-secondary/50"
                  {...form.register("email")}
                />
              </div>
            </FormItem>

            <FormItem>
              <FormLabel htmlFor="password" className="text-xs font-semibold text-muted-foreground">
                PASSWORD
              </FormLabel>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  className="h-12 pl-10 rounded-xl border-border bg-secondary/50"
                  {...form.register("password")}
                />
              </div>
            </FormItem>

            <button
              type="button"
              onClick={() => onSubmit(activeTab === 'signin' ? 'signIn' : 'create', form.getValues())}
              className="w-full h-12 rounded-xl text-sm font-semibold text-white mt-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: 'hsl(var(--tmw-accent))' }}
            >
              {activeTab === 'signin' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
        </Form>
      </div>

      {/* Footer text */}
      <p className="text-xs text-muted-foreground mt-6 text-center">
        {activeTab === 'signin'
          ? "Don't have an account? Tap Create Account above"
          : "Already have an account? Tap Sign In above"
        }
      </p>
    </div>
  );
};

